'use client';

/** @jsxRuntime classic */
/** @jsx jsx */

import { jsx } from '@emotion/react';
import Button from '@egalite/ui/Buttons';
import ErrorList from '@egalite/ui/ErrorList';
import { Check, InputField } from '@egalite/ui/Form';
import SelectField from '@egalite/ui/Form/SelectField';
import { useNotification } from '@egalite/ui/IncluiPCD/NotificationProvider';
import Loading from '@egalite/ui/Loading';
import Typography from '@egalite/ui/Typography';
import dateMonthMask from '@egalite/ui/dateMask/dateMonthMask';
import Cookies from 'js-cookie';
import { useRouter, useSearchParams } from 'next/navigation';
import { FC, Fragment, useCallback, useEffect, useState } from 'react';
import authFetch from '../../lib/auth/authFetch';
import useForm from '../../lib/hooks/useForm';
import validationSchema, { initialValues } from '../../lib/validation/candidate/professionalDataExperience';
import ButtonsPages from './legacy_blocks/ButtonsPages';
import FormPage from './legacy_blocks/CandidateFormPage';
import save from './save';

const ProfessionalDataExperienceForm: FC<{edit?: boolean, onSuccess?: () => void}> = ({ edit = false, onSuccess = null }) => {

	const notification = useNotification();
	const [loadingData, setLoadingData] = useState(true);
	const [candidateData, setCandidateData] = useState(null);
	const [experienceData, setExperienceData] = useState(initialValues);
	const searchParams = useSearchParams();
	const router = useRouter();

	const showNoExperience = useCallback(() => {
		if (candidateData.workExperience == null || candidateData.workExperience.length === 0) {
			return true;
		}
		return false;
	}, [candidateData]);

	const [formController, getFieldProps, { loading, submitting, success, errors, data }] = useForm({
		initialValues: experienceData,
		validationSchema,
		mutation: data => save('/api/v1/candidate/professionalDataExperience', data),
		onSuccess: onSuccess != null ? () => onSuccess() : () => {
			router.push(`/candidato/form/professionalDataExperienceIndex`, {scroll: true});
		},
	});

	useEffect(() => {
		if (loadingData) {
			authFetch('/api/v2/candidate/profile', Cookies.get('token'))
				.then(res => res.json())
				.then(data => {
					setLoadingData(false);
					if (data.success === true) {
						setCandidateData(data.data);
						const n = searchParams.get('n');
						if (n == null) {
							
						} else if (data.data.workExperience && data.data.workExperience[n] != null) {
							const experienceData = data.data.workExperience[n];
							const newValues = Object.keys(initialValues).reduce((acc, key) => {
								acc[key] = experienceData[key] ?? initialValues[key];
								return acc;
							}, {});
							(newValues as typeof initialValues).id = n;
							setExperienceData(newValues as typeof initialValues);
						} else {
							notification.error('Erro ao carregar dados do candidato');
						}
					} else {
						notification.error('Erro ao carregar dados do candidato');
					}
				});
		}
	}, [formController, router, loadingData, notification, searchParams]);

	if (loadingData) {
		return <Loading fullScreen={true}>carregando dados</Loading>;
	}

	return (
		<FormPage
			actualStep={3}
			formActualStep={5}
			formTotalSteps={5}
			progress={100}
			formTitle={'Experiência Profissional'}
			// previousForm="/"
			showStepsMenu={false}
			// showBackButton={false}
			modal={edit}
		>
			<form onSubmit={formController.handleSubmit}>
				<div className="mb-16 md:mb-24">
					<Typography variant="headline-5" className="text-brand-main mb-16" >
						Experiência Profissional
					</Typography>

					{edit === false && showNoExperience() ? (
						<Check
							id="checkExperience"
							labelProps={{ css: { alignSelf: 'center' } }}
							{...getFieldProps({ name: 'checkExperience', type: 'checkbox' })}
						>
							<Typography variant="body2" component="span">
								Não tenho experiência
							</Typography>
						</Check>
					) : null}

					{formController.values?.checkExperience === true ? null : (
						<Fragment>
							<InputField id="company" type="text" label="Empresa" {...getFieldProps('company')} />
							<InputField
								id="startDate"
								type="text"
								placeholder="Mês / Ano"
								maskOptions={dateMonthMask}
								label="Data de início"
								{...getFieldProps('startDate')}
							/>
							<InputField
								id="endDate"
								type="text"
								placeholder="Mês / Ano"
								maskOptions={dateMonthMask}
								label="Data de término"
								disabled={formController.values.checkActualJob === true}
								{...getFieldProps('endDate')}
							/>
							<Check
								id="checkActualJob"
								labelProps={{ css: { alignSelf: 'center' } }}
								{...getFieldProps({ name: 'checkActualJob', type: 'checkbox' })}
							>
								<Typography variant="body2" component="span">
									Este é meu emprego atual
								</Typography>
							</Check>
							<SelectField
								id="sector"
								placeholder="selecione..."
								label="Área de atuação"
								options={[
									{ value: 'Administrativo', label: 'Administrativo' },
									{ value: 'Almoxarifado', label: 'Almoxarifado' },
									{ value: 'Atendimento', label: 'Atendimento' },
									{ value: 'Auditoria', label: 'Auditoria' },
									{ value: 'Call center', label: 'Call center' },
									{ value: 'Comercial', label: 'Comercial' },
									{ value: 'Compliance', label: 'Compliance' },
									{ value: 'Compras', label: 'Compras' },
									{ value: 'Construção', label: 'Construção' },
									{ value: 'Fiscal', label: 'Fiscal' },
									{ value: 'Alimentação', label: 'Alimentação' },
									{ value: 'Educação', label: 'Educação' },
									{ value: 'Elétrica/eletrônica', label: 'Elétrica/eletrônica' },
									{ value: 'Engenharia', label: 'Engenharia' },
									{ value: 'Financeiro', label: 'Financeiro' },
									{ value: 'Indústria', label: 'Indústria' },
									{ value: 'Jurídico', label: 'Jurídico' },
									{ value: 'Logística', label: 'Logística' },
									{ value: 'Manutenção', label: 'Manutenção' },
									{ value: 'Marketing', label: 'Marketing' },
									{ value: 'Produção', label: 'Produção' },
									{ value: 'Qualidade', label: 'Qualidade' },
									{ value: 'Recursos Humanos', label: 'Recursos Humanos' },
									{ value: 'Saúde', label: 'Saúde' },
									{ value: 'Segurança do trabalho', label: 'Segurança do trabalho' },
									{ value: 'Serviços gerais', label: 'Serviços gerais' },
									{ value: 'Tecnologia (TI)', label: 'Tecnologia (TI)' },
									{ value: 'Portaria/Segurança', label: 'Portaria/Segurança' },
									{ value: 'Outro', label: 'Outro' },
								].sort((a, b) => (a.label > b.label ? 1 : -1))}
								{...getFieldProps('sector')}
							/>
							<InputField
								id="activities"
								name="activities"
								type="text"
								placeholder=""
								multiline={true}
								label="Atividades realizadas"
								message="Lorem ipsum dolor sit amet, consetetur sadipscing elitr."
								{...getFieldProps('activities')}
							/>
							<InputField
								id="latestRole"
								type="text"
								required={false}
								label="Último cargo"
								{...getFieldProps('latestRole')}
							/>
						</Fragment>
					)}

					<ErrorList errors={formController.errors} touched={formController.touched} />

					{errors != null &&
						errors.map((err, index) => (
							<Typography key={`err-${index}`} variant="body2" color="error.dark">
								{err}
							</Typography>
						))}
					<div className='md:mt-24 mt-16'>
						{formController.values?.checkExperience ? (
							<ButtonsPages
								next="/candidato/form/disabilityDataIndex"
								previousForm={'/candidato/form/professionalData4'}
								hideBack={edit}
							/>
						) : (
							edit ?
								<Button type='submit' variant='primary' className='w-full'>Salvar</Button>
							:
								<ButtonsPages
									submitDisabled={submitting}
									previousForm={'/candidato/form/professionalData4'}
									hideBack={edit}
								/>
						)}
					</div>
				</div>
			</form>
		</FormPage>
	);
};

export default ProfessionalDataExperienceForm;
